"use client";

import { useEffect, useMemo } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from "@/components/ui/field";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircleIcon, AlertTriangleIcon } from "lucide-react";
import { VWFS_LESSOR_SIRETS } from "@/lib/cat/vwfs-lessor-siret";
import { useNonVwfsLessorsQuery } from "@/lib/queries";
import type { GetNonVwfsLessorsResponse } from "@/app/api/non-vwfs-lessors/route";
import type { FundingType } from "@/prisma/generated/client";

type NonVwfsLessorRow = GetNonVwfsLessorsResponse["nonVwfsLessors"][number];

function formatNonVwfsLessorLabel(
  row: Pick<NonVwfsLessorRow, "name" | "siret">,
) {
  const name = (row.name ?? "").trim();
  const siret = (row.siret ?? "").trim();
  return `${name} (${siret})`;
}

export interface NonVwfsLessorFieldProps {
  fundingType: FundingType | null;
  siret: string;
  mdmNumber: string;
  onSiretChange: (siret: string) => void;
  onMdmNumberChange: (mdmNumber: string) => void;
  /** Shown below the combobox (e.g. VWFS SIRET conflict on a restored draft). */
  validationError?: string;
  onValidationErrorChange?: (message: string) => void;
  inputId?: string;
}

export function NonVwfsLessorField({
  fundingType,
  siret,
  mdmNumber,
  onSiretChange,
  onMdmNumberChange,
  validationError = "",
  onValidationErrorChange,
  inputId = "lessorSiret",
}: NonVwfsLessorFieldProps) {
  const isOtherFunding = fundingType === "OTHER";

  const nonVwfsLessorsQuery = useNonVwfsLessorsQuery(
    { sort: { name: "asc" } },
    undefined,
    { enabled: isOtherFunding },
  );

  const lessorOptions = useMemo(
    () =>
      (nonVwfsLessorsQuery.data?.nonVwfsLessors ?? []).filter(
        (lessor) => lessor.isActive,
      ),
    [nonVwfsLessorsQuery.data?.nonVwfsLessors],
  );

  const selectedLessor = useMemo(
    () => lessorOptions.find((lessor) => lessor.siret === siret) ?? null,
    [lessorOptions, siret],
  );

  const showQueryError = nonVwfsLessorsQuery.isError;
  const showEmptyList =
    nonVwfsLessorsQuery.isSuccess && lessorOptions.length === 0;

  useEffect(() => {
    if (!isOtherFunding || !selectedLessor) {
      return;
    }
    if (selectedLessor.mdmNumber !== mdmNumber) {
      onMdmNumberChange(selectedLessor.mdmNumber);
    }
  }, [isOtherFunding, mdmNumber, onMdmNumberChange, selectedLessor]);

  useEffect(() => {
    if (!onValidationErrorChange) {
      return;
    }
    if (!isOtherFunding) {
      onValidationErrorChange("");
      return;
    }
    if (!siret) {
      onValidationErrorChange("");
      return;
    }
    if (VWFS_LESSOR_SIRETS.includes(siret)) {
      onValidationErrorChange(
        "Ce SIRET correspond à VWFS. Veuillez sélectionner 'Financement VWFS' dans le type de financement.",
      );
      return;
    }
    if (nonVwfsLessorsQuery.isSuccess && !selectedLessor) {
      onValidationErrorChange(
        "Le loueur enregistré n'est plus disponible. Veuillez en sélectionner un autre.",
      );
      return;
    }
    if (nonVwfsLessorsQuery.isSuccess && selectedLessor) {
      onValidationErrorChange("");
    }
  }, [
    isOtherFunding,
    nonVwfsLessorsQuery.isSuccess,
    onValidationErrorChange,
    selectedLessor,
    siret,
  ]);

  if (!isOtherFunding) {
    return null;
  }

  const handleLessorChange = (next: NonVwfsLessorRow | null) => {
    onSiretChange(next?.siret ?? "");
    onMdmNumberChange(next?.mdmNumber ?? "");
    onValidationErrorChange?.("");
  };

  return (
    <Field>
      <FieldLabel htmlFor={inputId}>Loueur *</FieldLabel>
      <FieldContent>
        {nonVwfsLessorsQuery.isPending ? (
          <Skeleton className="h-9 w-full max-w-md" />
        ) : showQueryError ? (
          <Alert variant="destructive">
            <AlertCircleIcon className="h-4 w-4" />
            <AlertDescription>
              Une erreur est survenue lors du chargement des loueurs. Veuillez
              réessayer plus tard.
            </AlertDescription>
          </Alert>
        ) : showEmptyList ? (
          <Alert variant="destructive">
            <AlertCircleIcon className="h-4 w-4" />
            <AlertDescription>
              Aucun loueur n&apos;est disponible. Contactez
              l&apos;administrateur pour configurer la liste des loueurs hors
              VWFS.
            </AlertDescription>
          </Alert>
        ) : (
          <Combobox
            items={lessorOptions}
            itemToStringLabel={(row) => formatNonVwfsLessorLabel(row)}
            value={selectedLessor}
            onValueChange={handleLessorChange}
          >
            <ComboboxInput
              id={inputId}
              placeholder="Rechercher un loueur…"
              showClear={selectedLessor !== null}
              aria-label="Rechercher un loueur hors VWFS"
              disabled={lessorOptions.length === 0}
              className="max-w-md"
            />
            <ComboboxContent>
              <ComboboxEmpty>Aucun loueur trouvé.</ComboboxEmpty>
              <ComboboxList>
                {(row) => (
                  <ComboboxItem key={row.id} value={row}>
                    {formatNonVwfsLessorLabel(row)}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        )}
        {validationError ? (
          <Alert variant="destructive" className="mt-2">
            <AlertTriangleIcon className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {validationError}
            </AlertDescription>
          </Alert>
        ) : null}
      </FieldContent>
      <FieldDescription className="text-sm text-gray-500">
        Loueur responsable du financement (hors VWFS)
      </FieldDescription>
    </Field>
  );
}
